import { cellAt } from './board.js';
import { getControlledRegion } from './region.js';
import { effectiveColor } from './specials.js';
import type { Board, Cell, Coord } from './types.js';
import { coordKey } from './types.js';

export interface FloodResult {
  readonly cells: readonly Coord[];
  readonly nextCells: readonly (readonly Cell[])[];
}

/**
 * Recolour the controlled region (4-connected, same active color, not locked)
 * to `target`. Locked cells inside the region keep their tile but
 * NEVER change color. Flipping cells freeze to `target` and lose their
 * altColor (they become normal cells from then on). `phase` is the
 * flipping side selector and only affects which cells JOIN the region.
 */
export const floodRegion = (board: Board, target: string, phase: 0 | 1 = 0): FloodResult => {
  const region = getControlledRegion(board, board.start, phase);
  const affected: Coord[] = [];
  const updates = new Map<string, Cell>();

  region.forEach((key) => {
    const [xs, ys] = key.split(',');
    const c: Coord = { x: Number(xs), y: Number(ys) };
    const cur = cellAt(board, c);
    if (cur.tile === 'locked') return;
    const cur_eff = effectiveColor(cur, phase);
    if (cur_eff === target && cur.tile !== 'flipping') return;
    const nextTile = cur.tile === 'flipping' ? 'normal' : cur.tile;
    updates.set(coordKey(c), { color: target, tile: nextTile });
    affected.push(c);
  });

  const nextCells = board.cells.map((row, y) =>
    row.map((cell, x) => {
      const u = updates.get(coordKey({ x, y }));
      return u ?? cell;
    }),
  );

  return { cells: affected, nextCells };
};
